import {
  AuditType,
  CourseAuditLogData,
  OrderAuditLogData,
  OrderPageAuditLogData,
  PassAuditLogData,
  PaymentEntityType,
  PaymentLinkAuditLogData,
  UserAuditLogData,
  VoucherAuditLogData,
} from '../api';
import { EventAuditLogData } from '../api/models/EventAuditLogData';

export type AuditLogData =
  | OrderAuditLogData
  | EventAuditLogData
  | VoucherAuditLogData
  | PaymentLinkAuditLogData
  | CourseAuditLogData
  | PassAuditLogData
  | UserAuditLogData
  | OrderPageAuditLogData;

export interface AuditLogFilters {
  auditTypes?: AuditType[];
  orderNumber?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: number;
}

export interface AuditLogTableProps {
  filters?: AuditLogFilters;
  hideActions?: boolean;
}

export type AuditLogWithType<T extends AuditType> = T extends
  | AuditType.ORDER_CREATED
  | AuditType.ORDER_UPDATED
  | AuditType.ORDER_DELETED
  | AuditType.ORDER_RESTORED
  | AuditType.PENDING_ORDER_CONFIRMED
  | AuditType.PENDING_ORDER_REJECTED
  | AuditType.CHECKIN_CREATED
  | AuditType.CHECKIN_UPDATED
  | AuditType.EXTERNAL_VOUCHER_VERIFIED
  | AuditType.EXTERNAL_VOUCHER_REJECTED
  | AuditType.PROJECT_SELECTION_UPDATED
  | AuditType.EXTRA_PAYMENT_UPDATED
  | AuditType.EXTRA_PAYMENT_DELETED
  ? OrderAuditLogData
  : T extends AuditType.EVENT_CREATED | AuditType.EVENT_UPDATED | AuditType.EVENT_DELETED
  ? EventAuditLogData
  : T extends
      | AuditType.ORDER_CONVERTED_TO_VOUCHER
      | AuditType.VOUCHER_CREATED_FROM_ORDER
      | AuditType.VOUCHER_CONVERTED_TO_ORDER
      | AuditType.ORDER_CREATED_FROM_VOUCHER
  ? VoucherAuditLogData
  : T extends AuditType.PAYMENT_LINK_CREATED | AuditType.PAYMENT_LINK_PAID
  ? PaymentLinkAuditLogData
  : T extends AuditType.COURSE_ORDER_CREATED | AuditType.COURSE_ORDER_PAID | AuditType.COURSE_ORDER_DELETED
  ? CourseAuditLogData
  : T extends
      | AuditType.ORDER_CREATED_FROM_PASS
      | AuditType.PASS_CREATED
      | AuditType.PASS_UPDATED
      | AuditType.PASS_PAUSED
      | AuditType.PASS_RESUMED
      | AuditType.PASS_ARCHIVED
      | AuditType.PASS_RENEWED
      | AuditType.PASS_RENEWAL_FAILED
      | AuditType.PASS_PUNCH_CANCELED
      | AuditType.PASS_PUNCH_RESTORED
  ? PassAuditLogData
  : T extends
      | AuditType.USER_CREATED
      | AuditType.USER_UPDATED
      | AuditType.USER_ROLE_CHANGED
      | AuditType.USER_BLOCKED
      | AuditType.USER_UNBLOCKED
      | AuditType.USER_PASSWORD_RESET
  ? UserAuditLogData
  : T extends
      | AuditType.ORDER_PAGE_UPDATED
      | AuditType.ORDER_PAGE_ARCHIVED
      | AuditType.ORDER_PAGE_RESTORED
      | AuditType.PASS_ORDER_PAGE_UPDATED
      | AuditType.PASS_ORDER_PAGE_ARCHIVED
  ? OrderPageAuditLogData
  : AuditLogData;

const REFUND_AUDIT_TYPES = new Set<AuditType>([
  AuditType.REFUND_SUCCEEDED,
  AuditType.REFUND_PENDING,
  AuditType.REFUND_FAILED,
  AuditType.REFUND_BLOCKED,
]);

const isRefundAuditLogOf = (log: AuditLogData, entityTypes: PaymentEntityType[]) =>
  REFUND_AUDIT_TYPES.has(log.audit_type) && !!log.refund?.entity_type && entityTypes.includes(log.refund.entity_type);

const VOUCHER_REFUND_ENTITY_TYPES = [PaymentEntityType.VOUCHER];
const PASS_REFUND_ENTITY_TYPES = [PaymentEntityType.PASS, PaymentEntityType.PASS_PAYMENT];
const ORDER_REFUND_ENTITY_TYPES = [
  PaymentEntityType.ORDER,
  PaymentEntityType.PAYMENT_LINK,
  PaymentEntityType.EXTRA_PAYMENT_LINE,
];

export const isOrderAuditLog = (log: AuditLogData): log is OrderAuditLogData => {
  if (isRefundAuditLogOf(log, ORDER_REFUND_ENTITY_TYPES)) {
    return true;
  }
  return [
    AuditType.ORDER_CREATED,
    AuditType.ORDER_UPDATED,
    AuditType.ORDER_DELETED,
    AuditType.ORDER_RESTORED,
    AuditType.PENDING_ORDER_CONFIRMED,
    AuditType.PENDING_ORDER_REJECTED,
    AuditType.CHECKIN_CREATED,
    AuditType.CHECKIN_UPDATED,
    AuditType.EXTERNAL_VOUCHER_VERIFIED,
    AuditType.EXTERNAL_VOUCHER_REJECTED,
    AuditType.PROJECT_SELECTION_UPDATED,
    AuditType.EXTRA_PAYMENT_UPDATED,
    AuditType.EXTRA_PAYMENT_DELETED,
  ].includes(log.audit_type);
};

export const isEventAuditLog = (log: AuditLogData): log is EventAuditLogData => {
  return [AuditType.EVENT_CREATED, AuditType.EVENT_UPDATED, AuditType.EVENT_DELETED].includes(log.audit_type);
};

export const isVoucherAuditLog = (log: AuditLogData): log is VoucherAuditLogData => {
  if (isRefundAuditLogOf(log, VOUCHER_REFUND_ENTITY_TYPES)) {
    return true;
  }
  return [
    AuditType.ORDER_CONVERTED_TO_VOUCHER,
    AuditType.VOUCHER_CREATED_FROM_ORDER,
    AuditType.VOUCHER_CONVERTED_TO_ORDER,
    AuditType.ORDER_CREATED_FROM_VOUCHER,
  ].includes(log.audit_type);
};

export const isPaymentLinkAuditLog = (log: AuditLogData): log is PaymentLinkAuditLogData => {
  return [AuditType.PAYMENT_LINK_CREATED, AuditType.PAYMENT_LINK_PAID].includes(log.audit_type);
};

export const isCourseAuditLog = (log: AuditLogData): log is CourseAuditLogData => {
  return [AuditType.COURSE_ORDER_CREATED, AuditType.COURSE_ORDER_PAID, AuditType.COURSE_ORDER_DELETED].includes(
    log.audit_type,
  );
};

export const PASS_ORDER_PAGE_AUDIT_TYPES = new Set<AuditType>([
  AuditType.PASS_ORDER_PAGE_UPDATED,
  AuditType.PASS_ORDER_PAGE_ARCHIVED,
]);

const ORDER_PAGE_AUDIT_TYPES = new Set<AuditType>([
  AuditType.ORDER_PAGE_UPDATED,
  AuditType.ORDER_PAGE_ARCHIVED,
  AuditType.ORDER_PAGE_RESTORED,
  ...PASS_ORDER_PAGE_AUDIT_TYPES,
]);

export const isOrderPageAuditLog = (log: AuditLogData): log is OrderPageAuditLogData =>
  ORDER_PAGE_AUDIT_TYPES.has(log.audit_type);

// Derived from the enum so newly added pass_* audit types don't silently fall through to "unknown entity".
const PASS_AUDIT_TYPES = new Set<AuditType>([
  AuditType.ORDER_CREATED_FROM_PASS,
  ...Object.values(AuditType).filter((type) => type.startsWith('pass_') && !PASS_ORDER_PAGE_AUDIT_TYPES.has(type)),
]);

export const isPassAuditLog = (log: AuditLogData): log is PassAuditLogData =>
  PASS_AUDIT_TYPES.has(log.audit_type) || isRefundAuditLogOf(log, PASS_REFUND_ENTITY_TYPES);

export const USER_AUDIT_TYPES = new Set<AuditType>([
  AuditType.USER_CREATED,
  AuditType.USER_UPDATED,
  AuditType.USER_ROLE_CHANGED,
  AuditType.USER_BLOCKED,
  AuditType.USER_UNBLOCKED,
  AuditType.USER_PASSWORD_RESET,
]);

export const isUserAuditLog = (log: AuditLogData): log is UserAuditLogData => USER_AUDIT_TYPES.has(log.audit_type);
